import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  return (
    <nav className="fixed w-full z-50 px-4 md:px-6 py-2 md:py-4">
      <div className="max-w-7xl mx-auto">
        <div className={`bg-[#FAF9F8] backdrop-blur-lg rounded-full px-4 md:px-8 py-3 transition-shadow duration-500 ${isScrolled ? 'shadow-lg' : ''}`}>
          <div className="flex justify-between items-center relative">
            <div className="flex items-center gap-2 z-10">
              <Link to="/">
                <img 
                  src="/lovable-uploads/ebba3bd6-6f73-4dd8-af55-81ad7b9c4ff9.png" 
                  alt="planniFi" 
                  className="h-5 md:h-7"
                />
              </Link>
            </div>
            
            <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex items-center justify-center gap-8">
              <Link to="/solutions" className="relative text-[#141413] group">
                Solutions
                <span className={`absolute -bottom-1 left-0 w-full h-[1px] bg-[#141413] transform transition-transform duration-300 ease-out origin-center ${isActivePath('/solutions') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </Link>
              <Link to="/pricing" className="relative text-[#141413] group">
                Pricing
                <span className={`absolute -bottom-1 left-0 w-full h-[1px] bg-[#141413] transform transition-transform duration-300 ease-out origin-center ${isActivePath('/pricing') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </Link>
              <Link to="/faq" className="relative text-[#141413] group">
                FAQ
                <span className={`absolute -bottom-1 left-0 w-full h-[1px] bg-[#141413] transform transition-transform duration-300 ease-out origin-center ${isActivePath('/faq') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </Link>
              <Link to="/company" className="relative text-[#141413] group">
                Company
                <span className={`absolute -bottom-1 left-0 w-full h-[1px] bg-[#141413] transform transition-transform duration-300 ease-out origin-center ${isActivePath('/company') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}></span>
              </Link>
            </div>

            <div className="flex items-center gap-2 md:gap-4">
              <div className="md:hidden flex items-center gap-2">
                <a 
                  href="https://calendly.com/plannifi-demo/30min?back=1"
                  className="bg-[#27B5FC] text-white text-xs tracking-wider px-3 py-1.5 rounded-full font-medium hover:shadow-lg transition-shadow duration-300"
                >
                  DEMO
                </a>
                <a 
                  href="https://app.plannifi.com/auth/login?view=sign_up"
                  className="bg-[#141413] text-white text-xs tracking-wider px-3 py-1.5 rounded-full font-medium hover:shadow-lg transition-shadow duration-300"
                >
                  SIGN UP
                </a>
                <DropdownMenu onOpenChange={setIsOpen}>
                  <DropdownMenuTrigger className="data-[state=open]:bg-transparent focus:ring-0 focus:ring-offset-0" asChild>
                    <button className="p-2 rounded-full focus:outline-none focus:ring-0 focus:ring-offset-0">
                      <div className="relative w-5 h-5">
                        <Menu className={`absolute inset-0 transition-all duration-300 ${
                          isOpen ? 'opacity-0 rotate-180' : 'opacity-100 rotate-0'
                        }`} />
                        <X className={`absolute inset-0 transition-all duration-300 ${
                          isOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-180'
                        }`} />
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white">
                    <DropdownMenuItem asChild>
                      <Link to="/solutions">Solutions</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/pricing">Pricing</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/faq">FAQ</Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/company">Company</Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-2" />
                    <DropdownMenuItem asChild>
                      <a href="https://calendly.com/plannifi-demo/30min?back=1">Free Demo</a>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <a href="https://app.plannifi.com/auth/login?view=sign_in">Sign in</a>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="hidden md:flex items-center gap-4">
                <a 
                  href="https://calendly.com/plannifi-demo/30min?back=1"
                  className="bg-[#27B5FC] text-white font-medium rounded-full px-4 py-1.5 hover:shadow-lg transition-shadow duration-300"
                >
                  Free Demo
                </a>
                <a 
                  href="https://app.plannifi.com/auth/login?view=sign_in"
                  className="text-[#141413] font-medium rounded-full px-4 py-1.5 border border-[#C8C8C9] hover:shadow-lg transition-shadow duration-300"
                >
                  Sign in
                </a>
                <a 
                  href="https://app.plannifi.com/auth/sign-up" 
                  className="bg-[#141413] text-white px-4 py-1.5 rounded-full font-medium hover:shadow-lg transition-shadow duration-300"
                >
                  Sign up
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
